import { post } from "../http/platformServer";
import LogisticsCompany from "@/enum/LogisticsCompany";
import OrderMergeSplitStatus from "../../enum/OrderMergeSplitStatus";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import EwaybillType from "@/enum/EwaybillType";
import GetWaybillNoMethod from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/GetWaybillNoMethod";
import OrderStatus from "@/enum/OrderStatus";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import ShippingAddress from "@/dto/ewaybillAccount/ShippingAddressDTO";
import { ProductMaxQuantityPerPackageDto } from "./setting/product";
import { cloneDeep, groupBy, remove } from "lodash";
import { tabId } from "../http/tabId";
import { uuid } from "@/utils";
import { isMergeOrder } from "@/utils/OrderUtils";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import {
    EwaybillOrderItemDto,
    EwaybillOrderPackageDto,
    splitByTotalQuantity,
    splitByWeight,
} from "./util/ewaybillSplitUtil";

interface EwaybillWaybillTemplateDto {
    id: number;
    name: string;
    logisticsCompany: LogisticsCompany;
    serviceOptions: string;
    // 打印模板的ID
    printTemplateId: string;
    printTemplateUrl: string;
    templateVersion: number;
}

export interface EwaybillAccountDto {
    id: number;
    shopId: number;
    type: EwaybillType;
    platformUsername: string;
    ewaybillShippingAddressIds: number[];
    name: string;
}

interface EwaybillShippingAddressDto {
    id: number;
    province: String;
    city: String;
    district: String;
    town: String;
    street: String;
}

interface ShippingAddressDto {
    id: number;
    shipperName: string;
    province: string;
    city: string;
    district: string;
    street: string;
    telephone: string;
    mobile: string;
}

export interface EwaybillChildOrderDto {
    originalId: string;
    status: OrderStatus;
    /** 该子订单是否参与生成电子面单(对应前端的isSelected) */
    isPrintingEwaybill: boolean;

    id: number;
    isWithoutOrderItems: boolean;
}
interface EwaybillOrderDto {
    id: number;

    shopId: number;

    /**
     * 订单状态(用来判断前端和数据库订单信息状态是否一致)
     */
    status: OrderStatus;

    /**
     * 订单合并拆分状态(用来判断前端和数据库订单信息状态是否一致)
     */
    mergeSplitStatus: OrderMergeSplitStatus;

    /**
     * 子订单数，如果订单为合并订单，将子订单数回传，用于判断合并订单子订单是否发生变化，如果变化需要中断打单
     */
    childOrderCount: number;

    /**
     * 订单的退款状态,用来判断前端、后端订单退款状态是否一致
     */
    isRefunding: boolean;

    /**
     * 是否全选了OrderItem
     */
    isAllOrderItemsSelected: boolean;

    /**
     * 用户前端所有选中的商品信息
     */
    orderItems: EwaybillOrderItemDto[];

    /**
     * 包裹数
     */
    packageCount: number;

    /**
     * 分包裹打印的商品信息，用于分包打印，如果整单打印该字段留空，packages的数量必须要和packageCount一致
     */
    packages: EwaybillOrderPackageDto[];

    childOrders?: EwaybillChildOrderDto[];

    ewaybillCount: number;

    /**
     * 是否打印过运单号
     * 2024/10/30有用户反馈打印过的订单，再次打印无提示弹窗，但是日志各个方面都显示应该有弹窗的，添加此参数后续好判断
     */
    isWaybillPrinted: boolean;

    customPrintingOrderItemInfo?: string;
}

function buildEwaybillOrderItem(orderItem: OrderItemDTO): EwaybillOrderItemDto {
    return {
        originalId: orderItem.originalId,
        quantity: orderItem.quantity,
        status: orderItem.status,
    };
}

const buildEwaybillOrderItems = (orderItems: OrderItemDTO[]): EwaybillOrderItemDto[] => {
    const ewaybillOrderItems: EwaybillOrderItemDto[] = [];

    if (!orderItems || orderItems.length === 0) {
        return [];
    }

    orderItems.forEach((orderItem) => {
        ewaybillOrderItems.push(buildEwaybillOrderItem(orderItem));
    });

    return ewaybillOrderItems;
};

const buildEwaybillOrderDto = (
    order: OrderDTO,
    selectedOrderItems: OrderItemDTO[],
    setting: EwaybillPackageSetting & {
        perPackageSettingMapping: Map<string, number>;
    },
) => {
    const { id, shopId, status, mergeSplitStatus, isRefunding } = order;
    const {
        copies,
        perPackageSettingMapping,
        printGiftInLastWaybill,
        specifiedSplitQuantity,
        maxWeightPerPackage,
        defaultUnitWeight,
    } = setting;

    const childOrderCount = order.childOrders ? order.childOrders.length : null;

    const orderItems = buildEwaybillOrderItems(selectedOrderItems);

    const selectedOrderItemsLength = selectedOrderItems ? selectedOrderItems.length : 0;

    const orderItemsLength = order.orderItems ? order.orderItems.length : 0;

    // 是否全选了OrderItem
    const isAllOrderItemsSelected = selectedOrderItemsLength === orderItemsLength;

    // 当前订单有一个商品设置过分包规则
    const hasPerPackageSetting = selectedOrderItems.find((orderItem) => {
        const skuOriginalId = orderItem.skuOriginalId;
        const productOriginalId = orderItem.productOriginalId;

        const perPackageCount =
            perPackageSettingMapping.get(skuOriginalId) || perPackageSettingMapping.get(productOriginalId);

        return !!perPackageCount;
    });

    /**
     * 是否只选中赠品
     */
    const isOnlySelectedGift = selectedOrderItems.every((it) => it.isGift);
    const enabledPrintGiftInLastWaybill = printGiftInLastWaybill && !isOnlySelectedGift;

    let packageCount: number;
    let packages: EwaybillOrderPackageDto[];

    // 订单内有一个商品设置了自动分包
    // 整个订单全部走分包逻辑
    if (hasPerPackageSetting) {
        let _packageCount = 0;
        const _packages: EwaybillOrderPackageDto[] = [];

        let selectedGiftOrderItems: OrderItemDTO[] = [];

        if (enabledPrintGiftInLastWaybill) {
            selectedGiftOrderItems = remove(selectedOrderItems, (orderItem) => {
                return !!orderItem.isGift;
            });
        }

        // 如果是合并订单
        // 先把相同的商品的orderItem分组, 再数量从大到小排序后依次按设置的分包规则进行分包
        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
            const groupedSameSkuOrderItems = groupBy(selectedOrderItems, (orderItem) => {
                return `${orderItem.productOriginalId}|${orderItem.skuOriginalId}`;
            });

            Object.values(groupedSameSkuOrderItems).forEach((orderItems) => {
                const skuOriginalId = orderItems[0].skuOriginalId;
                const productOriginalId = orderItems[0].productOriginalId;

                let perPackageCount =
                    perPackageSettingMapping.get(skuOriginalId) || perPackageSettingMapping.get(productOriginalId);

                // 存在一个数量,则按此数量去分割包裹
                if (perPackageCount) {
                    // 如果分包数量大于0，代表{prePackageCount}个商品打印一个快递单
                    if (perPackageCount > 0) {
                        // 克隆一个新的orderItems, 然后再数量从大到小排序
                        const newOrderItems = cloneDeep(orderItems).sort((a, b) => b.quantity - a.quantity);
                        const allQuantity = orderItems.reduce((count, it) => it.quantity + count, 0);

                        const currentPackageCount = Math.ceil(allQuantity / perPackageCount);

                        for (let i = 0; i < currentPackageCount; i++) {
                            let counter = 0;
                            let currentPackage: EwaybillOrderPackageDto = {
                                orderItems: [],
                            };

                            loop2: for (let j = 0; j < newOrderItems.length; j++) {
                                const orderItem = newOrderItems[j];

                                if (counter + orderItem.quantity <= perPackageCount) {
                                    counter = counter + orderItem.quantity;

                                    currentPackage.orderItems.push({
                                        originalId: orderItem.originalId,
                                        quantity: orderItem.quantity,
                                        status: orderItem.status,
                                    });

                                    newOrderItems.splice(j, 1);

                                    // 如果总数已满足条件,则跳出循环
                                    if (counter === perPackageCount) {
                                        break loop2;
                                    } else {
                                        j--;
                                    }
                                }
                                // 这里差值数量必定大于0
                                else {
                                    const deltaQuantity = perPackageCount - counter;
                                    currentPackage.orderItems.push({
                                        originalId: orderItem.originalId,
                                        quantity: deltaQuantity,
                                        status: orderItem.status,
                                    });
                                    orderItem.quantity = orderItem.quantity - deltaQuantity;

                                    break loop2;
                                }
                            }

                            _packages.push(currentPackage);
                        }

                        _packageCount += currentPackageCount;
                    }
                    // 如果分包数量小于0，代表1个商品打印{abs(prePackageCount)}个快递单
                    // 注意prePackageCount是个负数，一定要Math.abs处理一下
                    else {
                        const allQuantity = orderItems.reduce((count, it) => it.quantity + count, 0);

                        const currentPackageCount = allQuantity * Math.abs(perPackageCount);

                        for (let i = 0; i < orderItems.length; i++) {
                            const orderItem = orderItems[i];

                            for (let j = 0; j < orderItem.quantity; j++) {
                                for (let n = 0; n < Math.abs(perPackageCount); n++) {
                                    let currentPackage: EwaybillOrderPackageDto = {
                                        orderItems: [
                                            {
                                                originalId: orderItem.originalId,
                                                // 此处不需要处理abs
                                                quantity: perPackageCount,
                                                status: orderItem.status,
                                            },
                                        ],
                                    };

                                    _packages.push(currentPackage);
                                }
                            }
                        }

                        _packageCount += currentPackageCount;
                    }
                }
                // 没有设置自动分包
                // 则所有相同的商品合并成一个快递包裹
                else {
                    const packageDto: EwaybillOrderPackageDto = {
                        orderItems: orderItems.map((it) => {
                            return {
                                originalId: it.originalId,
                                quantity: it.quantity,
                                status: it.status,
                            };
                        }),
                    };

                    _packageCount += 1;
                    _packages.push(packageDto);
                }
            });
        }
        // 独立订单
        else {
            selectedOrderItems.forEach((orderItem) => {
                const skuOriginalId = orderItem.skuOriginalId;
                const productOriginalId = orderItem.productOriginalId;

                let perPackageCount =
                    perPackageSettingMapping.get(skuOriginalId) || perPackageSettingMapping.get(productOriginalId);

                // 存在一个数量,则按此数量去分割包裹
                if (perPackageCount) {
                    // 如果分包数量大于0，代表{prePackageCount}个商品打印一个快递单
                    if (perPackageCount > 0) {
                        const currentPackageCount = Math.floor(orderItem.quantity / perPackageCount);

                        const remainingOrderItemQuantity = orderItem.quantity % perPackageCount;

                        for (let i = 0; i < currentPackageCount; i++) {
                            _packageCount++;
                            _packages.push({
                                orderItems: [
                                    {
                                        originalId: orderItem.originalId,
                                        quantity: perPackageCount,
                                        status: orderItem.status,
                                    },
                                ],
                            });
                        }

                        // 如果还有剩余的数量 再次新增一个包裹
                        if (remainingOrderItemQuantity > 0) {
                            _packageCount++;
                            _packages.push({
                                orderItems: [
                                    {
                                        originalId: orderItem.originalId,
                                        quantity: remainingOrderItemQuantity,
                                        status: orderItem.status,
                                    },
                                ],
                            });
                        }
                    }
                    // 如果分包数量小于0，代表1个商品打印{abs(prePackageCount)}个快递单
                    // 注意prePackageCount是个负数，一定要Math.abs处理一下
                    else {
                        const currentPackageCount = orderItem.quantity * Math.abs(perPackageCount);

                        for (let j = 0; j < orderItem.quantity; j++) {
                            for (let n = 0; n < Math.abs(perPackageCount); n++) {
                                let currentPackage: EwaybillOrderPackageDto = {
                                    orderItems: [
                                        {
                                            originalId: orderItem.originalId,
                                            // 此处不需要处理abs
                                            quantity: perPackageCount,
                                            status: orderItem.status,
                                        },
                                    ],
                                };

                                _packages.push(currentPackage);
                            }
                        }

                        _packageCount += currentPackageCount;
                    }
                }
                // 如果没有设置分包规则
                else {
                    _packageCount++;
                    _packages.push({
                        orderItems: [
                            {
                                originalId: orderItem.originalId,
                                quantity: orderItem.quantity,
                                status: orderItem.status,
                            },
                        ],
                    });
                }
            });
        }

        // 如果设置了把赠品打印到最后一张面单
        if (enabledPrintGiftInLastWaybill && selectedGiftOrderItems.length) {
            // 如果有成功分包，则直接放到最后一个包裹中去
            if (_packageCount) {
                const lastPackage = _packages[_packages.length - 1];

                selectedGiftOrderItems.forEach((it) => {
                    lastPackage.orderItems.push({
                        originalId: it.originalId,
                        quantity: it.quantity,
                        status: it.status,
                    });
                });
            } else {
                _packageCount = 1;
                _packages.push({
                    orderItems: selectedGiftOrderItems.map((it) => {
                        return {
                            originalId: it.originalId,
                            quantity: it.quantity,
                            status: it.status,
                        };
                    }),
                });
            }
        }

        packageCount = _packageCount;
        packages = _packages;
    }
    // 按总数量分包：每张快递单打印固定数量的商品
    else if (specifiedSplitQuantity && specifiedSplitQuantity > 0) {
        const result = splitByTotalQuantity(selectedOrderItems, specifiedSplitQuantity, enabledPrintGiftInLastWaybill);
        packageCount = result.packageCount;
        packages = result.packages;
    }
    // 按重量分包：根据商品重量分包
    else if (maxWeightPerPackage && maxWeightPerPackage > 0) {
        const result = splitByWeight(selectedOrderItems, maxWeightPerPackage, defaultUnitWeight || 0);
        packageCount = result.packageCount;
        packages = result.packages;
    }
    // 最后则默认走整单
    else {
        packageCount = copies;
        packages = null;
    }

    const orderDto: EwaybillOrderDto = {
        id,
        shopId,
        status,
        mergeSplitStatus,
        isRefunding,
        childOrderCount,
        isAllOrderItemsSelected,
        orderItems,
        packageCount,
        packages,
        ewaybillCount: order.ewaybills?.length,
        isWaybillPrinted: order.isWaybillPrinted,
        customPrintingOrderItemInfo: order.customPrintingOrderItemInfo,
    };

    if (isMergeOrder(order)) {
        orderDto.childOrders = order.childOrders.map(({ originalId, status, isSelected, id, orderItems }) => {
            return {
                originalId,
                status,
                isPrintingEwaybill: isSelected,
                id,
                isWithoutOrderItems: !orderItems || orderItems?.length === 0,
            };
        });
    }

    return orderDto;
};

/**
 * 获取合并订单选中的orderItems
 */
const getSelectedOrderItemsOfMergeParentOrder = (order: OrderDTO) => {
    const selectedOrderItems: OrderItemDTO[] = [];

    order.orderItems.forEach((orderItem) => {
        // @ts-ignore
        if (orderItem.isSelected) {
            selectedOrderItems.push(orderItem);
        }
    });

    return selectedOrderItems;
};

/**
 * 简化order数据，只把需要的数据传给后台,提升网络传送性能
 */
const buildEwaybillOrderDtos = (params: { orders: OrderDTO[] } & EwaybillPackageSetting) => {
    const { orders, ...setting } = params;

    const perPackageSettingMapping = new Map<string, number>();

    setting.productMaxQuantityPerPackages?.forEach((productSetting) => {
        if (productSetting?.skus?.length) {
            productSetting?.skus?.forEach((skuSetting) => {
                perPackageSettingMapping.set(skuSetting.originalId, skuSetting.maxQuantityPerPackage);
            });
        } else {
            perPackageSettingMapping.set(productSetting.originalId, productSetting.maxQuantityPerPackage);
        }
    });

    return orders.map((order) => {
        let selectedOrderItems = [];

        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
            selectedOrderItems = getSelectedOrderItemsOfMergeParentOrder(order);
        } else {
            // @ts-ignore
            selectedOrderItems = order.orderItems.filter((orderItem) => orderItem.isSelected);
        }

        return buildEwaybillOrderDto(order, selectedOrderItems, {
            ...setting,
            perPackageSettingMapping,
        });
    });
};

function buildEwaybillWaybillTemplateDto(waybillTemplate: WaybillTemplateDTO): EwaybillWaybillTemplateDto {
    return {
        id: waybillTemplate.id,
        name: waybillTemplate.name,
        logisticsCompany: waybillTemplate.logisticsCompany,
        printTemplateId: waybillTemplate.printTemplateId,
        serviceOptions: waybillTemplate.serviceOptions,
        printTemplateUrl: waybillTemplate.printTemplateUrl,
        templateVersion: waybillTemplate.templateVersion,
    };
}

export function buildEwaybillAccountDto(
    ewaybillShippingAddressId: number,
    ewaybillAccountMapping: Map<number, import("@/dto/ewaybillAccount/EwaybillAccountDTO").default>,
): EwaybillAccountDto | undefined {
    const ewaybillAccount = ewaybillAccountMapping.get(ewaybillShippingAddressId);

    if (!ewaybillAccount) {
        return undefined;
    }

    const ewaybillShippingAddressIds = [];

    ewaybillAccount.subscriptions.forEach((subscription) => {
        subscription.branchAccounts.forEach((branchAccount) => {
            branchAccount.shippingAddresses.forEach((shippingAddress) => {
                ewaybillShippingAddressIds.push(shippingAddress.id);
            });
        });
    });

    return {
        id: ewaybillAccount.id,
        shopId: ewaybillAccount.shopId,
        type: ewaybillAccount.type,
        platformUsername: ewaybillAccount.platformUsername,
        ewaybillShippingAddressIds: ewaybillShippingAddressIds,
        name: ewaybillAccount.name,
    };
}

export function buildEwaybillShippingAddressDto(
    ewaybillShippingAddressId: number,
    ewaybillShippingAddressMapping: Map<number, ShippingAddress>,
): EwaybillShippingAddressDto | undefined {
    const ewaybillShippingAddress = ewaybillShippingAddressMapping.get(ewaybillShippingAddressId);

    if (!ewaybillShippingAddress) {
        return undefined;
    }

    return {
        id: ewaybillShippingAddress.id,
        province: ewaybillShippingAddress.province,
        city: ewaybillShippingAddress.city,
        district: ewaybillShippingAddress.district,
        town: ewaybillShippingAddress.town,
        street: ewaybillShippingAddress.street,
    };
}

export interface EwaybillGenerateResult {
    errors: string[];

    ewaybills: EwaybillDTO[];

    orderId: number;
}

interface EwaybillGenerateParams extends Omit<GenerateParams, "orders" | "waybillTemplate" | "copies"> {
    orders: EwaybillOrderDto[];

    waybillTemplate: EwaybillWaybillTemplateDto;

    /** 后端用于判断是否重复提交 */
    reqId: string;
}

interface EwaybillPackageSetting {
    /**
     * 打印份数
     */
    copies?: number;

    specifiedSplitQuantity?: number;

    maxWeightPerPackage?: number;
    defaultUnitWeight?: number;

    productMaxQuantityPerPackages?: ProductMaxQuantityPerPackageDto[];

    printGiftInLastWaybill?: boolean;
}

export enum PackageCountType {
    /** 固定份数 */
    FIXED_COUNT = "FIXED_COUNT",
    /** 一个商品数量一个面单 */
    EACH_ITEM_QUANTITY = "EACH_ITEM_QUANTITY",
    /** 自动分包/智能分包 */
    INTELLIGENT_SPLIT = "INTELLIGENT_SPLIT",
    /** 拆分数量 */
    SPLIT_BY_SPECIFIED_QUANTITY = "SPLIT_BY_SPECIFIED_QUANTITY",
    /** 按重量拆分 */
    SPLIT_BY_WEIGHT = "SPLIT_BY_WEIGHT",
}

/**
 * 生成快递单号的页面
 */
export enum PrintShippingSource {
    SCAN_PRINT = "SCAN_PRINT",
    PURCHASING_LABEL = "PURCHASING_LABEL",
}

export interface GenerateParams extends EwaybillPackageSetting {
    orders: OrderDTO[];

    waybillTemplate: WaybillTemplateDTO;

    /**
     * 打印机名称，用于保存操作日志
     */
    printerName: string;

    /**
     * 发货易中的发货地址，打印到面单上
     */
    shippingAddress: ShippingAddressDto;

    /**
     * 获取单号的方式：新单号、旧单号
     */
    getWaybillNoMethod: GetWaybillNoMethod;

    /**
     * 是否使用子母单模式
     */
    useChildWaybillNoMode: boolean;

    /**
     * 只获取单号不打印
     */
    isGenerateWaybillNoOnly: boolean;

    /**
     * 该参数不为null，则优先使用该单号重打(扫描打印中使用)
     */
    ewaybillNo?: string;

    ewaybillAccount: EwaybillAccountDto;

    ewaybillShippingAddress: EwaybillShippingAddressDto;

    /**
     * 一边获取单号一边打印快递单
     */
    generateAndPrintSameTime?: boolean;

    /**
     * 不限制相同订单短时间重复打印
     */
    ignoreDuplicateOrderValidation?: boolean;

    /**
     * 保证本次任务唯一的Id，如果是在弹窗内，需要确保弹窗内的请求的taskId都一致,如果是非弹窗场景，需要每次请求是唯一值
     */
    taskId: string;

    packageCountType: PackageCountType;

    /**
     * 调用来源
     */
    invocationSource?: PrintShippingSource;

    /**
     * 所有拿货标签扫描完成后是否自动打印
     */
    autoPrintAfterAllPurchasingLabelScanned?: boolean;
    /**
     * 打印完成后是否自动发货
     */
    autoShipAfterPrinting?: boolean;

    /**
     * 打印批次号
     */
    printBatchNo?: string;

    shippingAddressSelectType?: ShippingAddressSelectType;
}

export enum ShippingAddressSelectType {
    FIXED = "FIXED", //  按选择的发货人地址 (可不传)
    BY_SHOP_BINDING = "BY_SHOP_BINDING", //  不同店铺打印不同发货地址
    BY_TEMPLATE_BINDING = "BY_TEMPLATE_BINDING", // 不同快递单模板打印不同发货地址
}

/**
 * 转换为请求后端的数据
 */
async function buildEwaybillGenerateParams(_params: GenerateParams): Promise<EwaybillGenerateParams> {
    const {
        orders,
        waybillTemplate,
        printerName,
        shippingAddress,
        getWaybillNoMethod,
        isGenerateWaybillNoOnly,
        ewaybillNo,
        useChildWaybillNoMode,
        copies,
        productMaxQuantityPerPackages,
        ewaybillAccount,
        ewaybillShippingAddress,
        printGiftInLastWaybill,
        generateAndPrintSameTime,
        ignoreDuplicateOrderValidation,
        taskId,
        packageCountType,
        invocationSource,
        autoPrintAfterAllPurchasingLabelScanned,
        autoShipAfterPrinting,
        printBatchNo,
        specifiedSplitQuantity,
        shippingAddressSelectType,
        maxWeightPerPackage,
        defaultUnitWeight,
    } = _params;

    const ewaybillOrderDtos: EwaybillOrderDto[] = buildEwaybillOrderDtos({
        orders,
        copies,
        productMaxQuantityPerPackages,
        printGiftInLastWaybill,
        specifiedSplitQuantity,
        maxWeightPerPackage,
        defaultUnitWeight,
    });

    const params: EwaybillGenerateParams = {
        orders: ewaybillOrderDtos,
        waybillTemplate: buildEwaybillWaybillTemplateDto(waybillTemplate),
        printerName,
        shippingAddress,
        getWaybillNoMethod,
        useChildWaybillNoMode,
        isGenerateWaybillNoOnly,
        ewaybillNo,
        specifiedSplitQuantity, // 后端用于记录拆分数量的日志，不涉及到计算
        maxWeightPerPackage,
        defaultUnitWeight,
        ewaybillAccount,
        ewaybillShippingAddress,
        generateAndPrintSameTime,
        ignoreDuplicateOrderValidation,
        // @ts-ignore
        tabId,
        taskId,
        packageCountType,
        printBatchNo,
        shippingAddressSelectType,
    };

    if (invocationSource === PrintShippingSource.PURCHASING_LABEL) {
        params.autoPrintAfterAllPurchasingLabelScanned = autoPrintAfterAllPurchasingLabelScanned;
        params.autoShipAfterPrinting = autoShipAfterPrinting;
    }

    const batchId = uuid();

    const reqId = (taskId || "") + "|" + batchId;

    params.reqId = reqId;

    return params;
}

/**
 * 同步生成电子面单
 */
export const syncGenerate = async (_params: GenerateParams) => {
    const shopId = _params.orders[0].shopId;

    const params = await buildEwaybillGenerateParams(_params);

    let path = "/ewaybill/generate";

    if (_params.invocationSource === PrintShippingSource.SCAN_PRINT) {
        path = "/scan_printing/ewaybill/generate";
    } else if (_params.invocationSource === PrintShippingSource.PURCHASING_LABEL) {
        path = "/purchasing_label/ewaybill/generate";
    }

    return post<EwaybillGenerateResult[]>(shopId, path, params);
};

export function submitAppendExtraWaybillToSellerRemarksTask(shopId: number, orderIds: number[]) {
    return post(shopId, "/order/submit_append_extra_waybill_to_to_seller_remarks_task", { shopId, orderIds });
}
